import { Link } from 'react-router-dom';
import {
  Users,
  Shield,
  CheckCircle,
  Trash2,
  MapPin,
  LayoutDashboard,
  Sheet,
  BarChart,
  Building2,
  Calendar,
  Tag,
  BookOpen,
  Layers,
} from 'lucide-react';
import Breadcrumb from './layout/Breadcrumb';

interface TileProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  link: string;
}

const AdminTile = ({ title, description, icon, link }: TileProps) => (
  <Link
    to={link}
    title={description}
    className="flex items-center gap-2.5 rounded-md border border-border bg-card px-3 py-2.5 text-sm font-medium text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/50 hover:text-primary group"
  >
    <div className="shrink-0 rounded-md bg-primary/10 p-1.5 text-primary group-hover:bg-primary/20 transition-colors">
      {icon}
    </div>
    <span className="truncate">{title}</span>
  </Link>
);

interface CategoryProps {
  label: string;
  items: TileProps[];
}

const AdminCategory = ({ label, items }: CategoryProps) => (
  <div className="flex flex-col gap-2">
    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</h3>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
      {items.map(item => <AdminTile key={item.link} {...item} />)}
    </div>
  </div>
);

const AdminDashboardPage = () => {
  const categories: CategoryProps[] = [
    {
      label: 'System',
      items: [
        { title: 'Analytics', description: 'View platform analytics, revenue, and user statistics', icon: <BarChart size={15} />, link: '/admin/settings' },
        { title: 'Users', description: 'Manage users, view details, and edit user information', icon: <Users size={15} />, link: '/admin/users' },
        { title: 'Permissions', description: 'Grant and manage user permissions across locations', icon: <Shield size={15} />, link: '/admin/permissions' },
        { title: 'Approvals', description: 'Review and approve pending content submissions', icon: <CheckCircle size={15} />, link: '/admin/approvals' },
        { title: 'Recycle Bin', description: 'View and restore deleted content', icon: <Trash2 size={15} />, link: '/admin/recycle-bin' },
      ],
    },
    {
      label: 'Organizations',
      items: [
        { title: 'Organizations', description: 'Create and manage organizations with hierarchy', icon: <Building2 size={15} />, link: '/admin/organizations' },
        { title: 'Org Types', description: 'Define and manage organization types for the platform', icon: <LayoutDashboard size={15} />, link: '/admin/organization-types' },
        { title: 'Org Sections', description: 'Configure sections and their hierarchy', icon: <Layers size={15} />, link: '/admin/sections' },
        { title: 'Location Names', description: 'Create and manage locations in the system', icon: <MapPin size={15} />, link: '/admin/taxonomy/location-names' },
      ],
    },
    {
      label: 'Services & Topics',
      items: [
        { title: 'Service Categories', description: 'Manage service type categories', icon: <Tag size={15} />, link: '/admin/service-types' },
        { title: 'Service Names', description: 'Define service templates and offerings', icon: <Sheet size={15} />, link: '/admin/service-definitions' },
        { title: 'Topic Categories', description: 'Manage topic categories', icon: <Tag size={15} />, link: '/admin/topic-types' },
        { title: 'Topic Names', description: 'Manage knowledge base topics and guidance', icon: <BookOpen size={15} />, link: '/admin/topics' },
      ],
    },
    {
      label: 'Occasions',
      items: [
        { title: 'Occasion Types', description: 'Define occasion categories', icon: <Tag size={15} />, link: '/admin/occasion-types' },
        { title: 'Occasions', description: 'Manage religious and cultural occasions', icon: <Calendar size={15} />, link: '/admin/occasions' },
      ],
    },
  ];

  return (
    <>
      <Breadcrumb pageName="Admin Dashboard" />
      <div className="flex flex-col gap-2 mb-5">
        <h2 className="text-xl font-bold text-foreground">Admin Dashboard</h2>
        <p className="text-sm text-muted-foreground">Manage all aspects of the App platform.</p>
      </div>
      <div className="flex flex-col gap-6">
        {categories.map(cat => <AdminCategory key={cat.label} {...cat} />)}
      </div>
    </>
  );
};

export default AdminDashboardPage;



